import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import axios from 'axios'
import toast from 'react-hot-toast'
import { FiArrowLeft, FiTrash2, FiCheck, FiX, FiRefreshCw, FiMail, FiFileText } from 'react-icons/fi'
import { formatFiatCryptoPair, formatCrypto, formatFiat } from '../../utils/amountFormatter'

export default function CryptoDashboard() {
  const { websiteId } = useParams()
  const navigate = useNavigate()

  const [transactions, setTransactions] = useState([])
  const [website, setWebsite] = useState(null)
  const [hasCryptoAccess, setHasCryptoAccess] = useState(null)
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState(null)
  const [filter, setFilter] = useState({ status: '', type: '' })
  const [updating, setUpdating] = useState(false)
  const [sendingConfirmation, setSendingConfirmation] = useState(false)
  const [sendingInvoice, setSendingInvoice] = useState(false)

  useEffect(() => {
    fetchTransactions()
  }, [websiteId, filter])

  const fetchTransactions = async () => {
    try {
      setLoading(true)
      const token = localStorage.getItem('userAdminToken') || localStorage.getItem('token') || localStorage.getItem('globalAdminToken')
      if (token) axios.defaults.headers.common['Authorization'] = `Bearer ${token}`

      const websiteResponse = await axios.get(`/websites/${websiteId}`)
      const websiteData = websiteResponse.data.website
      setWebsite(websiteData)
      const approvedCryptoProduct = (websiteData.extraPremium?.isActive || websiteData.extraPremium?.requestStatus === 'approved') && websiteData.extraPremium?.allowedSections?.cryptoProduct
      const accessAllowed = !websiteData.appPremiumMode || approvedCryptoProduct
      setHasCryptoAccess(accessAllowed)

      if (!accessAllowed) {
        return
      }

      const params = new URLSearchParams()
      if (filter.status) params.append('status', filter.status)
      if (filter.type) params.append('type', filter.type)

      const res = await axios.get(`/crypto/website/${websiteId}?${params}`)
      setTransactions(res.data.transactions || [])
    } catch (err) {
      console.error('Error fetching crypto transactions:', err)
      if (err.response?.status === 403) {
        setHasCryptoAccess(false)
      }
      toast.error(err.response?.data?.message || 'Failed to load transactions')
    } finally {
      setLoading(false)
    }
  }

  const updateStatus = async (transactionId, status) => {
    try {
      setUpdating(true)
      await axios.patch(`/crypto/${transactionId}/status`, { status })
      toast.success('Status updated')
      setTransactions(prev => prev.map(t => t._id === transactionId ? { ...t, status } : t))
      if (selected?._id === transactionId) setSelected(prev => ({ ...prev, status }))
    } catch (err) {
      console.error('Error updating status:', err)
      toast.error('Failed to update status')
    } finally {
      setUpdating(false)
    }
  }

  const deleteTransaction = async (transactionId) => {
    if (!window.confirm('Delete this transaction? This action cannot be undone.')) return
    try {
      await axios.delete(`/crypto/${transactionId}`)
      toast.success('Transaction deleted')
      setTransactions(prev => prev.filter(t => t._id !== transactionId))
      if (selected?._id === transactionId) setSelected(null)
    } catch (err) {
      console.error('Error deleting transaction:', err)
      toast.error('Failed to delete transaction')
    }
  }

  const sendPaidConfirmation = async (transactionId) => {
    try {
      // Use per-transaction sending state to prevent global blocking and multiple clicks
      setTransactions(prev => prev.map(t => t._id === transactionId ? { ...t, _sendingConfirmation: true } : t))
      if (selected?._id === transactionId) setSendingConfirmation(true)

      const response = await axios.post(`/crypto/${transactionId}/send-confirmation`)

      toast.success('Confirmation email sent successfully')
      setTransactions(prev => prev.map(t => t._id === transactionId ? {
        ...t,
        confirmationSent: true,
        confirmationSentAt: response.data.confirmationSentAt,
        status: response.data.status || 'payment_successful',
        formattedAmount: response.data.formattedAmount || t.formattedAmount,
        formattedCrypto: response.data.formattedCrypto || t.formattedCrypto,
        _sendingConfirmation: false
      } : t
      ))

      if (selected?._id === transactionId) {
        setSelected(prev => ({
          ...prev,
          confirmationSent: true,
          confirmationSentAt: response.data.confirmationSentAt,
          status: response.data.status || 'payment_successful',
          formattedAmount: response.data.formattedAmount || prev.formattedAmount,
          formattedCrypto: response.data.formattedCrypto || prev.formattedCrypto
        }))
      }
    } catch (err) {
      console.error('Error sending confirmation:', err)
      toast.error(err.response?.data?.message || 'Failed to send confirmation email')
      setTransactions(prev => prev.map(t => t._id === transactionId ? { ...t, _sendingConfirmation: false } : t))
    } finally {
      if (selected?._id === transactionId) setSendingConfirmation(false)
    }
  }

  const sendInvoice = async (transactionId) => {
    try {
      setSendingInvoice(true)
      const response = await axios.post(`/crypto/${transactionId}/send-invoice`)
      toast.success('Invoice sent successfully')
      setTransactions(prev => prev.map(t => 
        t._id === transactionId ? { ...t, invoiceSent: true, invoiceSentAt: response.data.invoiceSentAt, formattedAmount: response.data.formattedAmount || t.formattedAmount, formattedCrypto: response.data.formattedCrypto || t.formattedCrypto } : t
      ))
      if (selected?._id === transactionId) {
        setSelected(prev => ({ ...prev, invoiceSent: true, invoiceSentAt: response.data.invoiceSentAt, formattedAmount: response.data.formattedAmount || prev.formattedAmount, formattedCrypto: response.data.formattedCrypto || prev.formattedCrypto }))
      }
    } catch (err) {
      console.error('Error sending invoice:', err)
      toast.error(err.response?.data?.message || 'Failed to send invoice')
    } finally {
      setSendingInvoice(false)
    }
  }

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-gray-100 text-gray-800',
      awaiting_payment: 'bg-blue-100 text-blue-800',
      payment_successful: 'bg-green-100 text-green-800',
      awaiting_crypto: 'bg-yellow-100 text-yellow-800',
      crypto_received: 'bg-purple-100 text-purple-800',
      pending_payout: 'bg-orange-100 text-orange-800',
      completed: 'bg-emerald-100 text-emerald-800',
      failed: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-800'
    }
    return colors[status] || 'bg-gray-100 text-gray-800'
  }

  const getConfirmationButtonLabel = (transaction) => {
    if (!transaction) return 'Send Confirmation'
    return transaction.confirmationSent ? 'Confirmation Sent' : `Send ${transaction.type === 'buy' ? 'Buy' : 'Sell'} Confirmation`
  }

  const formatStatus = (status) => {
    return status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
  }

  if (loading) return <div className="min-h-screen flex items-center justify-center">Loading...</div>
  if (hasCryptoAccess === false) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="max-w-xl w-full bg-white rounded-xl shadow-lg border border-gray-200 p-8 text-center">
        <div className="text-5xl mb-4">🔒</div>
        <h2 className="text-2xl font-bold text-gray-900 mb-3">Crypto Access Restricted</h2>
        <p className="text-gray-600 mb-6">
          The crypto dashboard is not available until the Crypto Product section is approved by Global Admin.
        </p>
        <button
          onClick={() => navigate('/website-builder')}
          className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
        >
          Back to Website Builder
        </button>
      </div>
    </div>
  )

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center gap-4">
          <button onClick={() => navigate('/user-admin/websites')} className="p-2 hover:bg-gray-100 rounded" title="Back to Website Builder Dashboard">
            <FiArrowLeft />
          </button>
          <h1 className="text-2xl font-bold">₿ Crypto Transactions</h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="flex gap-4 items-center">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={filter.status}
                onChange={(e) => setFilter(prev => ({ ...prev, status: e.target.value }))}
                className="border border-gray-300 rounded px-3 py-2 text-sm"
              >
                <option value="">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="awaiting_payment">Awaiting Payment</option>
                <option value="payment_successful">Payment Successful</option>
                <option value="awaiting_crypto">Awaiting Crypto</option>
                <option value="crypto_received">Crypto Received</option>
                <option value="pending_payout">Pending Payout</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select
                value={filter.type}
                onChange={(e) => setFilter(prev => ({ ...prev, type: e.target.value }))}
                className="border border-gray-300 rounded px-3 py-2 text-sm"
              >
                <option value="">All Types</option>
                <option value="buy">Buy</option>
                <option value="sell">Sell</option>
              </select>
            </div>
            <button
              onClick={fetchTransactions}
              className="mt-6 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-2"
            >
              <FiRefreshCw className="w-4 h-4" /> Refresh
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1 bg-white rounded-lg shadow p-4">
            <h2 className="font-semibold mb-4">Transactions ({transactions.length})</h2>
            {transactions.length === 0 ? (
              <div className="text-gray-500">No transactions found</div>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {transactions.map(t => (
                  <div
                    key={t._id}
                    onClick={() => setSelected(t)}
                    className={`w-full text-left p-3 rounded border ${selected?._id === t._id ? 'bg-blue-50 border-blue-300' : 'hover:bg-gray-50 border-gray-200'} cursor-pointer`}
                  >
                    <div className="flex flex-col gap-2">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <div className="font-semibold">{t.orderId}</div>
                          <div className="text-xs text-gray-500">{t.formattedAmount || (t.fiatAmount && t.cryptoAmount ? formatFiatCryptoPair(t.fiatAmount, t.cryptoAmount, t.cryptoType) : '')}</div>
                          <div className="text-xs text-gray-400">{new Date(t.createdAt).toLocaleString()}</div>
                        </div>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation()
                            sendPaidConfirmation(t._id)
                          }}
                          disabled={t._sendingConfirmation || t.confirmationSent || !['awaiting_payment','awaiting_crypto','crypto_received','payment_successful','pending_payout'].includes(t.status)}
                          className="text-xs bg-blue-600 text-white rounded-full px-3 py-1 hover:bg-blue-700 disabled:opacity-50"
                        >
                          {t._sendingConfirmation ? 'Sending...' : getConfirmationButtonLabel(t)}
                        </button>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(t.status)}`}>
                          {formatStatus(t.status)}
                        </span>
                        {t.invoiceSent && (
                          <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-800">Invoice Sent</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="lg:col-span-2 bg-white rounded-lg shadow p-6">
            {!selected ? (
              <div className="text-gray-500">Select a transaction to view details</div>
            ) : (
              <div>
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <h3 className="text-xl font-bold">{selected.orderId}</h3>
                    <div className="text-sm text-gray-500">{selected.type.toUpperCase()} {selected.cryptoType}</div>
                    <div className="text-xs text-gray-500 mt-1">{new Date(selected.createdAt).toLocaleString()}</div>
                  </div>
                  <div className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(selected.status)}`}>
                    {formatStatus(selected.status)}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold mb-3">User Details</h4>
                    <div className="space-y-2 text-sm">
                      <div><span className="font-medium">Name:</span> {selected.userDetails.fullName}</div>
                      <div><span className="font-medium">Email:</span> {selected.userDetails.email}</div>
                      <div><span className="font-medium">Phone:</span> {selected.userDetails.phone}</div>
                      {selected.type === 'sell' && selected.userDetails.accountName && (
                        <div><span className="font-medium">Account Name:</span> {selected.userDetails.accountName}</div>
                      )}
                      {selected.type === 'sell' && selected.userDetails.bankName && (
                        <div><span className="font-medium">Bank:</span> {selected.userDetails.bankName}</div>
                      )}
                      {selected.type === 'sell' && selected.userDetails.accountNumber && (
                        <div><span className="font-medium">Account Number:</span> {selected.userDetails.accountNumber}</div>
                      )}
                      {selected.type === 'buy' && selected.userDetails.walletAddress && (
                        <div><span className="font-medium">Wallet Address:</span> {selected.userDetails.walletAddress}</div>
                      )}
                      {selected.userDetails.paymentMethod && (
                        <div><span className="font-medium">Payment Method:</span> {selected.userDetails.paymentMethod}</div>
                      )}
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-3">Transaction Details</h4>
                    <div className="space-y-2 text-sm">
                      <div><span className="font-medium">Amount:</span> {selected.formattedAmount || formatFiatCryptoPair(selected.fiatAmount, selected.cryptoAmount, selected.cryptoType)}</div>
                      <div><span className="font-medium">Fiat Amount:</span> {formatFiat(selected.fiatAmount)}</div>
                      <div><span className="font-medium">Applied Rate:</span> {formatFiat(selected.appliedRate)} per {selected.cryptoType}</div>
                      {selected.walletAddress && <div><span className="font-medium">Wallet:</span> {selected.walletAddress.substring(0, 20)}...</div>}
                      {selected.txid && <div><span className="font-medium">TXID:</span> {selected.txid.substring(0, 20)}...</div>}
                    </div>
                  </div>
                </div>

                {selected.qrCode && (
                  <div className="mt-6">
                    <h4 className="font-semibold mb-3">Payment QR Code</h4>
                    <img src={selected.qrCode} alt="Payment QR Code" className="w-32 h-32" />
                  </div>
                )}

                {selected.rateLockedUntil && (
                  <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
                    <p className="text-sm text-yellow-800">
                      <strong>Rate Locked Until:</strong> {new Date(selected.rateLockedUntil).toLocaleString()}
                    </p>
                  </div>
                )}

                {/* Confirmation & Invoice Section */}
                <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded">
                  <h4 className="font-semibold text-blue-900 mb-3">Payment Confirmation</h4>
                  <button
                    onClick={() => sendPaidConfirmation(selected._id)}
                    disabled={selected._sendingConfirmation || sendingConfirmation || selected.confirmationSent || !['awaiting_payment','awaiting_crypto','crypto_received','payment_successful','pending_payout'].includes(selected.status)}
                    className="w-full px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    <FiMail className="w-4 h-4" />
                    {selected._sendingConfirmation ? 'Sending...' : getConfirmationButtonLabel(selected)}
                  </button>
                  {selected.confirmationSent && selected.confirmationSentAt && (
                    <p className="text-xs text-blue-700 mt-2">
                      Sent at {new Date(selected.confirmationSentAt).toLocaleString()}
                    </p>
                  )}
                  {!selected.confirmationSent && selected.status !== 'awaiting_payment' && (
                    <p className="text-xs text-gray-500 mt-2">
                      Confirmation can only be sent when transaction status is Awaiting Payment.
                    </p>
                  )}
                </div>

                {selected.confirmationSent && !selected.invoiceSent && (
                  <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded">
                    <h4 className="font-semibold text-green-900 mb-3">Send Invoice</h4>
                    <button
                      onClick={() => sendInvoice(selected._id)}
                      disabled={sendingInvoice}
                      className="w-full px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      <FiFileText className="w-4 h-4" />
                      Send Invoice
                    </button>
                  </div>
                )}

                {selected.invoiceSent && (
                  <div className="mt-4 p-3 bg-emerald-50 border border-emerald-200 rounded">
                    <p className="text-sm text-emerald-800">
                      ✓ Invoice sent at {new Date(selected.invoiceSentAt).toLocaleString()}
                    </p>
                  </div>
                )}

                <div className="mt-6 flex gap-2">
                  {selected.status === 'crypto_received' && (
                    <button
                      onClick={() => updateStatus(selected._id, 'pending_payout')}
                      disabled={updating}
                      className="px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 disabled:opacity-50"
                    >
                      Mark for Payout
                    </button>
                  )}
                  {selected.status === 'pending_payout' && (
                    <button
                      onClick={() => updateStatus(selected._id, 'completed')}
                      disabled={updating}
                      className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                    >
                      Mark Completed
                    </button>
                  )}
                  <button
                    onClick={() => updateStatus(selected._id, 'cancelled')}
                    disabled={updating}
                    className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => deleteTransaction(selected._id)}
                    className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-700"
                  >
                    <FiTrash2 className="w-4 h-4" />
                  </button>
                </div>

                {selected.adminNotes && (
                  <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded">
                    <h4 className="font-semibold text-sm mb-1">Admin Notes</h4>
                    <p className="text-sm text-gray-700">{selected.adminNotes}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}